/**
 * The seven headline numbers on the Skida app dashboard, all period-relative.
 *
 * Every box answers "how many, in the period you picked" — pick All time and
 * you get all of them. Clicking a box asks the same question again, bucketed,
 * to draw the series.
 *
 * ## What each one is actually counting
 *
 * Five come from subscription *state* in `skidaCrmSubscriptions`, which the
 * RevenueCat backfill keeps current. The distinction that makes them possible
 * is holding both `startsAtMs` (when the subscription first began) and
 * `currentPeriodStartsAtMs` (when its current period began) — with only one of
 * those, a renewal is indistinguishable from a new subscription.
 *
 *   newUsers        `users.createdAt` in period
 *   newTrials       `startsAtMs` in period, still a live trial
 *   directSubs      `startsAtMs` in period, paid store, gross revenue booked
 *   lostCustomers   people whose last access ended in period (see below)
 *   renewedSubs     `currentPeriodStartsAtMs` in period and later than `startsAtMs`
 *
 * The TRY column reads state too, from 25 Aug:
 *
 *   trialsConverting   live trial now, willRenew true
 *   trialsCancelling   live trial now, willRenew false
 *
 * Together they are every live trial, split by whether auto-renewal is on —
 * what is set to happen to the trials running now, which is the actionable
 * question.
 *
 * ## A trial is `periodType == 'TRIAL'`, never `status == 'trialing'`
 *
 * Corrected 3 Sep 2026, and it is not a preference between two equivalent
 * fields. **Only the backfill writes `status`.** `scripts/backfillRevenueCat.ts`
 * copies it from RevenueCat's v2 subscription object; the webhook writes
 * `periodType`, `expiresAtMs` and `willRenew` on every event and never touches
 * `status` at all. So `status` is a snapshot of whenever the last full pass ran,
 * and it does not move when a trial converts — the subscription becomes
 * `periodType: NORMAL` while `status` still says `trialing` for ever.
 *
 * What that produced on this dashboard, measured on 3 Sep:
 *
 *   status == 'trialing'                27   the TRY column
 *   periodType == 'TRIAL' && live       41   the funnel immediately below it
 *   RevenueCat's own active trials      19
 *
 * Of the 27, six had an `expiresAtMs` already in the past. Of the 41, twenty-six
 * carried no `status` field at all — written by a code path that never set one —
 * so the TRY column could not see them however long it waited. Two boxes on one
 * screen, same words, and neither number reachable from the other.
 *
 * Every trial query below therefore filters on `periodType`, and liveness is
 * `expiresAtMs > now` compared against the clock. Both are fields the webhook
 * maintains. `status` is left on the documents — the backfill still writes it
 * and something may yet want the vintage — but nothing reads it.
 *
 * Two cannot come from state at all, and this is worth being blunt about:
 *
 *   cancelledTrials, convertedTrials
 *
 * A trial that converted looks exactly like a subscription that never had a
 * trial — RevenueCat's subscription object carries only the *current* status,
 * not the transitions. The only record of a transition is the webhook's event
 * log, which starts the day the webhook went live. So these two count real
 * events, from `skidaCrmSubscriptionEvents`, and are honest about their
 * horizon rather than showing a plausible-looking zero. They left the journey
 * row on 25 Aug and are still measured — history the mirror cannot otherwise
 * recover is worth keeping even when it is not what the row is read for.
 *
 * ## Cost
 *
 * Each figure is one `getCountFromServer` — a count comes back as a number, and
 * nothing else crosses the wire. A bucketed series is one count per bucket,
 * capped at 24, so the most expensive thing a user can do here is 24 counts.
 */
import {
  collection,
  getCountFromServer,
  query,
  where,
  type QueryConstraint,
} from 'firebase/firestore'
import { db, productDb } from '@/shared/firebase/client'
import { CRM_COLLECTIONS, PRODUCT_COLLECTIONS } from '@/shared/firebase/collections'

export type MetricKey =
  | 'newUsers'
  | 'newTrials'
  | 'trialsConverting'
  | 'trialsCancelling'
  | 'cancelledTrials'
  | 'convertedTrials'
  | 'directSubs'
  | 'appTrialsRunning'
  | 'appTrialsConverted'
  | 'appTrialsExpired'
  | 'lostCustomers'
  | 'renewedSubs'

/**
 * Where a box sits in the customer's journey.
 *
 * Used only to group and colour the row. The order of `METRICS` is the journey
 * itself — arrive, try, decide, stay, leave — so the boxes read left to right
 * as one story rather than as seven unrelated counts.
 */
export type FlowStage = 'arrive' | 'try' | 'buy' | 'stay' | 'leave'

export const FLOW_STAGES: { key: FlowStage; label: string }[] = [
  { key: 'arrive', label: 'Arrive' },
  { key: 'try', label: 'Try' },
  { key: 'buy', label: 'Buy' },
  { key: 'stay', label: 'Stay' },
  { key: 'leave', label: 'Leave' },
]

export interface MetricDef {
  key: MetricKey
  label: string
  /** One line under the number: what it counts, in the user's terms. */
  sub: string
  color: string
  /** Where it sits on the journey row, or `null` for a metric kept off it. */
  stage: FlowStage | null
  /** The longer explanation, shown in the chart dialog and behind the (i). */
  basis: string
  /** The honest limit of the figure, if it has one. Rendered under a rule. */
  caveat?: string
  /**
   * True when the figure can only cover the period since the webhook went
   * live, because it counts transitions rather than state.
   */
  eventsOnly?: boolean
  /**
   * True when the figure is *as of now* and the range filter does not apply.
   *
   * "How many trials are running right now" has no period in it. Scoping it to
   * the range — which is what this row does to everything else — gave a number
   * that shrank when somebody narrowed the filter and disagreed with the funnel
   * directly below showing the same words. Worse, it looked plausible.
   *
   * So these boxes ignore the window, say so on their face, and get no
   * sparkline: a series of eight identical values is a chart of nothing.
   */
  pointInTime?: boolean
}

/**
 * In journey order: arrive → try → buy → stay → leave.
 *
 * The previous order put "Cancelled trials" before "Converted trials" and
 * dropped "Renewed" at the end after "Expired", which made the row read as a
 * list of facts rather than as a process. Nothing about the counting changed
 * here; only the sequence and the wording.
 */
export const METRICS: MetricDef[] = [
  {
    key: 'newUsers',
    label: 'New accounts',
    sub: 'signed up for skida.app',
    color: 'var(--color-primary)',
    stage: 'arrive',
    basis:
      'Consumer accounts created in the period — the top of the funnel. All time counts the collection outright rather than ranging on the creation date, because roughly 17,000 older accounts carry no creation date and Firestore cannot return a document that lacks the field it is ranging over. So the headline is the real user base.',
    caveat:
      'The chart has to bucket by date, so it can only plot accounts that carry one. The dialog says how many fall outside it.',
  },
  {
    key: 'newTrials',
    label: 'Trials running',
    sub: 'started in the period, still on trial',
    color: 'var(--color-warning)',
    // Off the journey row from 17 Aug: TRY shows the two transitions, converted
    // above cancelled, and the live trial is already split three ways on the
    // funnel directly below — the row was saying it twice. Still measured, still
    // charted, and still read by anything asking for the whole metric list.
    stage: null,
    basis:
      'Subscriptions that began in the period and are *still* on a trial today — the trial period has not run out yet. This is a state, not a count of trials started: one that has since converted is no longer a trial, and one that lapsed has an expiry date in the past, so neither is in this box.',
    caveat:
      'How many trials started can never be recovered from state — on conversion the period type flips from TRIAL to NORMAL and nothing records that a trial happened. Only the webhook, from the day it went live, can count starts.',
  },
  /**
   * ---- What TRY shows, from 25 Aug ----
   *
   * The two boxes below replaced the event-based pair on the row. They answer
   * *"what is happening to the trials running right now"* rather than *"what
   * became of the trials we have a webhook record for"* — which is the question
   * somebody looking at this row is actually asking, and the one they can act on:
   * a trial set to cancel is still winnable.
   *
   * **They are scoped to the range, like everything else on this row.** A trial
   * that started inside it and is still running today, split by whether
   * auto-renewal is on. So the two sum to *Trials running* exactly, and the
   * range filter and the sparkline both mean something — an as-of-today total
   * would ignore both controls sitting above it.
   *
   * That makes them narrower than the funnel's own split directly below, which
   * is every live trial regardless of when it started. Same words, two scopes,
   * and the (i) on each box says which. The funnel is the standing picture; this
   * is what the chosen period produced.
   */
  /**
   * Skida 3.0's own trial — 26 Oct 2026. Every account gets Premium for the
   * days set in `AppSettings/settings.initialTrial` from when its onboarding
   * completes; no store is involved, so RevenueCat never sees it. Mirrored by
   * `crmTick` (appTrials.ts) into `skidaCrmAppTrials`, correlated with the
   * RevenueCat mirror: a purchase during the trial moves the account out of
   * this box and into New subscriptions.
   */
  {
    key: 'appTrialsRunning',
    label: 'App trials',
    sub: 'on Skida’s own trial now',
    color: 'var(--color-indigo)',
    stage: 'try',
    pointInTime: true,
    basis:
      'Accounts inside Skida’s own free trial right now: Premium for the number of days set in AppSettings, from when their onboarding completed in the app. Not a store trial — the two RevenueCat boxes beside this count those. Somebody who buys during it leaves this box within half an hour and counts as a new subscription. As of now, not for the selected range.',
    caveat:
      'Mirrored every 30 minutes. Existing subscribers who updated to 3.0 also complete onboarding, and are left out: they had Premium already. So are Skida PRO members, whose Premium comes with PRO — they are counted under PRO subscribers.',
  },
  {
    key: 'trialsConverting',
    label: 'Active trials converting',
    sub: 'on trial now, auto-renewal on',
    color: 'var(--color-success)',
    stage: 'try',
    pointInTime: true,
    basis:
      'Trials running right now with auto-renewal switched on — unless somebody changes their mind, each becomes a paying subscription when the trial ends. Counted as a trial whose period has not yet expired, so a subscription the mirror has not heard about since it lapsed cannot be counted as live. As of now, not for the selected range.',
    caveat:
      'Renewal intent is a setting, not a promise: a person can switch it off on the last day, and a card can fail. It says what is set to happen, not what will. Cross-checked against RevenueCat’s own active-trial total — the box says so when the two disagree.',
  },
  {
    key: 'trialsCancelling',
    label: 'Active trials cancelled',
    sub: 'on trial now, auto-renewal off',
    color: 'var(--color-warning)',
    stage: 'try',
    pointInTime: true,
    basis:
      'Trials running right now with auto-renewal switched off — they still have access, and unless something changes they will lapse rather than convert. These are the ones worth a message while the trial is still live, which is the whole reason this box replaced a count of trials that had already ended.',
    caveat:
      'Access has not ended yet, so these people are still users today — this is not a churn figure. Switching auto-renewal back on moves somebody into the box above with no other event.',
  },
  {
    key: 'convertedTrials',
    label: 'Trials converted',
    sub: 'trial became a paid subscription',
    color: 'var(--color-success)',
    // Off the row from 25 Aug, and kept measured — the same treatment
    // `newTrials` got on 17 Aug. It answers a question about history that state
    // cannot answer at all, so deleting it would throw away the only record of
    // a conversion the mirror keeps. It is simply not what the TRY column is
    // for: the row is read to decide what to do next.
    stage: null,
    eventsOnly: true,
    basis:
      'Trials that turned into paying subscriptions, counted from the webhook’s own event log using RevenueCat’s isTrialConversion flag. State cannot answer this: once the conversion is done, the subscription is indistinguishable from one that never had a trial.',
    caveat:
      'Events only exist from the day the webhook went live. A small number here means a short history, not a poor conversion rate.',
  },
  {
    key: 'cancelledTrials',
    label: 'Trials cancelled',
    sub: 'ended without converting',
    color: 'var(--color-error)',
    // Off the row from 25 Aug — see the note on `convertedTrials`.
    stage: null,
    eventsOnly: true,
    basis:
      'Trials that were cancelled or expired without ever becoming paid. Also from webhook events — a cancelled trial leaves no trace at all on the subscription document.',
    caveat:
      'Events only exist from the day the webhook went live. A small number here means a short history, not low churn.',
  },
  {
    key: 'directSubs',
    label: 'New subscriptions',
    sub: 'started paying in the period',
    color: 'var(--color-success)',
    stage: 'buy',
    basis:
      'Subscriptions that began paying in the period. Counted as purchased-minus-promotional, because RevenueCat’s ownership field says “purchased” even for a promotional grant — it means “not family-shared”, not “somebody paid”. The store is what separates money from a gift.',
    caveat:
      'Cannot be split into bought-outright versus converted-from-trial: from the store’s side both are the same event, and this figure is computed from subscription state. The Trials converted box answers that question separately, for the period the webhook covers.',
  },
  {
    key: 'appTrialsConverted',
    label: 'App trials bought',
    sub: 'bought during the app trial',
    color: 'var(--color-success)',
    stage: 'buy',
    basis:
      'Accounts that bought a subscription while on Skida’s own trial, by when they bought. Each is also counted in New subscriptions — this says how many of those came out of the app trial.',
  },
  {
    key: 'renewedSubs',
    label: 'Renewed',
    sub: 'rolled into a new period',
    color: 'var(--color-teal)',
    stage: 'stay',
    basis:
      'Subscriptions whose current period began inside the period and later than the subscription itself — they renewed rather than started. This is the recurring base doing its job.',
    caveat:
      'Only the most recent renewal per subscription is visible, so a yearly subscription renewed twice counts once.',
  },
  {
    key: 'appTrialsExpired',
    label: 'App trials ended',
    sub: 'ran out without buying',
    color: 'var(--color-grey)',
    stage: 'leave',
    basis:
      'Accounts whose Skida trial ran out in the period without a purchase. They are on Free now, and are exactly who the trial-ended emails are for.',
  },
  {
    key: 'lostCustomers',
    label: 'Customers lost',
    sub: 'last access ended in the period',
    color: 'var(--color-error)',
    stage: 'leave',
    basis:
      'People whose subscription access ended inside the period and who have none now — counted per person from the subscriber rollup, not per subscription. Bounded by “already past” as well as by the period, so somebody whose access merely runs out later is not reported as having left.',
    caveat:
      'A renewal is invisible here, which is the point: RevenueCat models every renewal term as its own subscription, so counting subscriptions that ended counted each renewal as a departure. On 4 Sep that read 48 against 35 real departures. Somebody who cancels but is still paid up is not here either — they appear under Active trials cancelled or keep access until the term runs out.',
  },
]

/** Inclusive-start, exclusive-end window in epoch ms. `from: null` = all time. */
export interface Window {
  from: number | null
  to: number
}

const subs = () => collection(db, CRM_COLLECTIONS.subscriptions)
const events = () => collection(db, CRM_COLLECTIONS.subscriptionEvents)
const users = () => collection(productDb, PRODUCT_COLLECTIONS.users)
/** Skida 3.0's own trial, mirrored by `crmTick` (appTrials.ts). */
const appTrials = () => collection(db, 'skidaCrmAppTrials')

async function countIn(
  ref: ReturnType<typeof subs>,
  field: string,
  win: Window,
  extra: QueryConstraint[] = [],
): Promise<number> {
  const range: QueryConstraint[] = [where(field, '<', win.to)]
  if (win.from !== null) range.unshift(where(field, '>=', win.from))
  const snap = await getCountFromServer(query(ref, ...extra, ...range))
  return snap.data().count
}

/**
 * One metric over one window.
 *
 * Most queries keep their range on a single field and express everything else as
 * an equality, which composes. `newTrials` is the exception and ranges on two —
 * "started in the window" and "has not expired yet" are both inequalities, and
 * Firestore has allowed several since late 2023 provided every ranged field is
 * in the index. See `sandbox, periodType, expiresAtMs, startsAtMs` in
 * `firestore.indexes.json`.
 */
export async function countMetric(key: MetricKey, win: Window, now: number): Promise<number> {
  switch (key) {
    case 'newUsers':
      // All time means all of them. A range on `createdAt` silently drops every
      // document that lacks the field — 17,466 of the accounts, mostly old ones
      // — so when there is no lower bound there is no reason to range at all.
      if (win.from === null && win.to >= now) {
        const snap = await getCountFromServer(query(users()))
        return snap.data().count
      }
      return countIn(users(), 'createdAt', win)

    case 'newTrials':
      // A trial that started in the window and is *still* one, which is what
      // the label says. `periodType` alone would keep counting it after it
      // lapsed: RevenueCat's EXPIRATION event carries `period_type: TRIAL`, so
      // the field stays TRIAL and only the expiry moves into the past.
      return countIn(subs(), 'startsAtMs', win, [
        where('sandbox', '==', false),
        where('periodType', '==', 'TRIAL'),
        where('expiresAtMs', '>', now),
      ])

    /**
     * Trials running *now*, split by renewal intent — and the expiry test is the
     * whole point of the query.
     *
     * `periodType == 'TRIAL'` alone is a claim the mirror made when it last
     * heard from RevenueCat, and on 26 Aug 2026 that was seventeen days earlier:
     * **eighteen of the twenty-five documents then marked as trials had already
     * expired.** The row was reporting 25 active trials when RevenueCat's own
     * total was 19. A period type is a memory; an expiry date is a fact, and
     * comparing it to the clock is what makes this figure degrade honestly when
     * the sync falls behind rather than inventing live trials.
     *
     * The type filter was `status == 'trialing'` until 3 Sep 2026 — see the
     * module header for why that field cannot be used for this.
     *
     * `expiresAtMs > now` is therefore not an optimisation — it is the
     * difference between a stale number and a wrong one.
     *
     * No window: see `pointInTime`. The pair is deliberately exhaustive —
     * `willRenew` is set on every document in the mirror — so the two boxes
     * together are every live trial, with nobody in a third bucket.
     */
    case 'appTrialsRunning': {
      // `endsAtMs > now` as well as the outcome: the mirror runs every half
      // hour, and a trial that ended since must not be counted as running.
      const snap = await getCountFromServer(
        query(appTrials(), where('outcome', '==', 'running'), where('endsAtMs', '>', now)),
      )
      return snap.data().count
    }
    case 'appTrialsConverted':
      return countIn(appTrials(), 'convertedAtMs', win)
    case 'appTrialsExpired':
      return countIn(appTrials(), 'endsAtMs', win, [where('outcome', '==', 'expired')])

    case 'trialsConverting':
      return countTrials(true, now)

    case 'trialsCancelling':
      return countTrials(false, now)

    case 'directSubs': {
      // Neither `givesAccess` nor `ownership` separates money from a gift.
      // `givesAccess` is true for a grant, and — the trap — so is
      // `ownership: "purchased"`: RevenueCat means "owned by this customer
      // rather than family-shared" by that field, not "somebody paid". All 319
      // promotional subscriptions in the mirror carry it.
      //
      // The store is what actually distinguishes them, but `store != …` is an
      // inequality and the window already spends the one range Firestore
      // allows. So: count the purchased ones, then subtract the promotional
      // ones in the same window. Every promotional row is also `purchased`, so
      // the subtraction is exact — and unlike an `in` list of known stores, it
      // stays correct when a new store appears.
      const [purchased, promotional] = await Promise.all([
        countIn(subs(), 'startsAtMs', win, [
          where('sandbox', '==', false),
          where('ownership', '==', 'purchased'),
        ]),
        countIn(subs(), 'startsAtMs', win, [
          where('sandbox', '==', false),
          where('store', '==', 'PROMOTIONAL'),
        ]),
      ])
      return Math.max(0, purchased - promotional)
    }

    /**
     * Departures, counted per **person** — 4 Sep 2026.
     *
     * This was `endsAtMs` on `skidaCrmSubscriptions`, and it could not have been
     * right. RevenueCat models **each renewal term as its own subscription
     * object**: asked for one customer its API returns last year's term as
     * `expired` beside this year's as `active`, each with its own store
     * identifier. So every renewal closed a subscription, and every renewal was
     * counted as an ending. The box read 48 for 28 days in which 35 people
     * actually left, and the same names appeared under Renewed and Expired on
     * the same date.
     *
     * There is no per-subscription repair for that. "Did somebody leave" is a
     * question about a person, and the answer is the rollup: `status` is
     * `lapsed` only when *no* subscription of theirs is still live, and
     * `maxExpiresAtMs` is the newest term's expiry — so a renewal moves the date
     * forward instead of leaving a departure behind, and the chain never passes
     * through `lapsed` at all.
     *
     * Bounded by "already past" as well as by the window, so somebody whose
     * access merely runs out later is not reported as having gone.
     */
    case 'lostCustomers': {
      const end = Math.min(win.to, now)
      if (win.from !== null && win.from >= end) return 0
      const range: QueryConstraint[] = [where('maxExpiresAtMs', '<', end)]
      if (win.from !== null) range.unshift(where('maxExpiresAtMs', '>=', win.from))
      const snap = await getCountFromServer(
        query(
          collection(db, CRM_COLLECTIONS.subscribers),
          where('sandbox', '==', false),
          where('status', '==', 'lapsed'),
          ...range,
        ),
      )
      return snap.data().count
    }

    case 'renewedSubs': {
      // "Renewed" means the current period started later than the subscription
      // did. Firestore cannot compare two fields, so the range narrows to the
      // window and the documents are checked in memory — capped, because this
      // is the one figure that cannot be a pure count.
      return countRenewals(win)
    }

    case 'cancelledTrials':
      return countEvents(['SUBSCRIPTION_PAUSED', 'CANCELLATION'], win, 'TRIAL')

    case 'convertedTrials':
      return countEvents(['SUBSCRIPTION_PAUSED', 'RENEWAL', 'INITIAL_PURCHASE'], win, 'CONVERT')
  }
}

/**
 * Live trials by renewal intent, as of now.
 *
 * One range (`expiresAtMs`) and three equalities, which is one composite index —
 * `sandbox, periodType, willRenew, expiresAtMs`. It counts on the server, so a
 * number comes back and nothing else crosses the wire.
 */
async function countTrials(willRenew: boolean, now: number): Promise<number> {
  const snap = await getCountFromServer(
    query(
      subs(),
      where('sandbox', '==', false),
      where('periodType', '==', 'TRIAL'),
      where('willRenew', '==', willRenew),
      where('expiresAtMs', '>', now),
    ),
  )
  return snap.data().count
}

/**
 * Renewals need a field-to-field comparison Firestore cannot express, so the
 * documents in the window are fetched and compared here. Two projected fields
 * per document, and the window bounds it — over a year of Skida's volume that
 * is a few thousand rows, not the collection.
 */
async function countRenewals(win: Window): Promise<number> {
  const { getDocs, orderBy, limit } = await import('firebase/firestore')
  const range: QueryConstraint[] = [where('currentPeriodStartsAtMs', '<', win.to)]
  if (win.from !== null) range.unshift(where('currentPeriodStartsAtMs', '>=', win.from))
  const snap = await getDocs(
    query(
      subs(),
      where('sandbox', '==', false),
      ...range,
      orderBy('currentPeriodStartsAtMs'),
      limit(10000),
    ),
  )
  let n = 0
  for (const d of snap.docs) {
    const cur = d.get('currentPeriodStartsAtMs')
    const start = d.get('startsAtMs')
    if (typeof cur === 'number' && typeof start === 'number' && cur > start) n++
  }
  return n
}

/**
 * Trial transitions, from the webhook's event log.
 *
 * `type` alone is not enough — a CANCELLATION on a paid subscription is churn,
 * not a cancelled trial — so the period type on the event decides. Events
 * arrive only from the day the webhook went live; the UI says so rather than
 * letting a small number read as a real one.
 */
async function countEvents(types: string[], win: Window, kind: 'TRIAL' | 'CONVERT'): Promise<number> {
  const { getDocs } = await import('firebase/firestore')
  const range: QueryConstraint[] = [where('eventAtMs', '<', win.to)]
  if (win.from !== null) range.unshift(where('eventAtMs', '>=', win.from))
  const snap = await getDocs(query(events(), where('sandbox', '==', false), ...range))
  let n = 0
  for (const d of snap.docs) {
    const type = String(d.get('type') ?? '')
    if (!types.includes(type) && type !== 'EXPIRATION') continue
    const period = String(d.get('periodType') ?? '').toUpperCase()
    if (kind === 'TRIAL') {
      // A trial that ended without converting.
      if (period === 'TRIAL' && (type === 'CANCELLATION' || type === 'EXPIRATION')) n++
    } else if (d.get('isTrialConversion') === true) {
      n++
    }
  }
  return n
}

/** Every metric for one window, in parallel. */
export interface MetricResults {
  /** The figure per box. Null only where `errors` carries a reason. */
  values: Record<MetricKey, number | null>
  /** Why a box has no figure, for the ones that failed. */
  errors: Partial<Record<MetricKey, string>>
}

/**
 * Every box, and for the ones that failed, why — 3 Sep 2026.
 *
 * One failing metric must still not blank the row, which is why each is caught
 * separately. What changed is that the reason is now carried out of here instead
 * of being dropped on the floor.
 *
 * `countMetric` either returns a number or throws, so a null value has never
 * meant "none in this period" — it has only ever meant the query failed. The row
 * rendered that as an em-dash, which is what a legitimately empty period would
 * look like, so a missing composite index and a quiet week were indistinguishable
 * on screen. Both this file and `metricRowsRepository` were changed on the same
 * day to query on `periodType` with new indexes behind them; a silent `—` is
 * exactly the wrong way to find out one of them is not deployed.
 */
export async function loadMetrics(
  win: Window,
  now: number = Date.now(),
): Promise<MetricResults> {
  const out: MetricResults = { values: {} as Record<MetricKey, number | null>, errors: {} }
  await Promise.all(
    METRICS.map(async (m) => {
      try {
        out.values[m.key] = await countMetric(m.key, win, now)
      } catch (err) {
        out.values[m.key] = null
        const msg = err instanceof Error ? err.message : String(err)
        // Firestore's index error is a paragraph with a console URL in it. The
        // first sentence is the part a reader can act on.
        out.errors[m.key] = /requires an index/i.test(msg)
          ? 'This figure needs a Firestore index that is missing or still building.'
          : msg.slice(0, 200)
      }
    }),
  )
  return out
}

/* -------------------------------------------------------------------------- */
/*  Bucketing                                                                  */
/* -------------------------------------------------------------------------- */

export type BucketUnit = 'hour' | 'day' | 'week' | 'month'

export interface Bucket {
  /** Window for this bucket. */
  from: number
  to: number
  label: string
}

const HOUR = 3_600_000
const DAY = 86_400_000

/**
 * Split a period into at most 24 buckets, choosing the unit from its length.
 *
 * The cap is what makes the chart readable *and* cheap: 24 points is enough to
 * see a shape and it bounds the query cost at 24 counts however long the period
 * is. The unit steps hour → day → week → month so a 24-hour period reads
 * hourly and five years reads monthly, without anyone choosing a granularity.
 */
export function bucketsFor(
  win: Window,
  now: number,
  cap = 24,
): { unit: BucketUnit; buckets: Bucket[] } {
  const from = win.from ?? now - 365 * DAY * 3
  const span = Math.max(HOUR, win.to - from)

  const unit: BucketUnit =
    span <= 2 * DAY ? 'hour' : span <= 60 * DAY ? 'day' : span <= 400 * DAY ? 'week' : 'month'

  const buckets: Bucket[] = []
  if (unit === 'month') {
    const cursor = new Date(from)
    cursor.setUTCDate(1)
    cursor.setUTCHours(0, 0, 0, 0)
    while (cursor.getTime() < win.to && buckets.length < cap) {
      const start = cursor.getTime()
      const next = new Date(cursor)
      next.setUTCMonth(next.getUTCMonth() + 1)
      buckets.push({
        from: Math.max(start, from),
        to: Math.min(next.getTime(), win.to),
        label: new Date(start).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
      })
      cursor.setUTCMonth(cursor.getUTCMonth() + 1)
    }
    // A very long all-time range would exceed the cap; keep the recent end,
    // which is the part anyone is actually reading.
    return { unit, buckets: buckets.slice(-cap) }
  }

  const size = unit === 'hour' ? HOUR : unit === 'day' ? DAY : 7 * DAY
  const count = Math.min(cap, Math.max(1, Math.ceil(span / size)))
  const step = Math.ceil(span / count)
  for (let i = 0; i < count; i++) {
    const start = from + i * step
    const end = Math.min(start + step, win.to)
    if (start >= win.to) break
    buckets.push({
      from: start,
      to: end,
      label:
        unit === 'hour'
          ? new Date(start).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
          : new Date(start).toLocaleDateString('en-US', { day: '2-digit', month: 'short' }),
    })
  }
  return { unit, buckets }
}

/* -------------------------------------------------------------------------- */
/*  Sparklines                                                                 */
/* -------------------------------------------------------------------------- */

/** Points per box. Eight is a shape; the dialog is where a series is read. */
const SPARK_BUCKETS = 8

/**
 * A shape for every box, cheaply enough to run on every range change.
 *
 * The dialog's `loadSeries` is one query per bucket per metric. Seven boxes at
 * eight buckets that way would be 56 round trips — and three of those metrics do
 * not answer to a count at all: renewals need a field-to-field comparison and
 * the two trial-transition figures come from the event log, so each of their
 * buckets would fetch documents.
 *
 * So the two shapes are computed differently on purpose. The four state metrics
 * bucket by counting, which is a number over the wire per bucket. The other
 * three fetch their window **once** and bucket in memory — the same documents
 * `countMetric` would have read, read once instead of eight times.
 *
 * A metric that fails resolves to null and its box simply has no sparkline; the
 * figure beside it is a separate query and stands on its own.
 */
export async function loadSparklines(
  win: Window,
  now: number = Date.now(),
): Promise<Record<MetricKey, number[] | null>> {
  const { buckets } = bucketsFor(win, now, SPARK_BUCKETS)
  const safe = async (fn: () => Promise<number[]>): Promise<number[] | null> => {
    try {
      return await fn()
    } catch {
      return null
    }
  }

  const counted = (key: MetricKey) =>
    safe(() =>
      Promise.all(buckets.map((b) => countMetric(key, { from: b.from, to: b.to }, now))),
    )

  const [newUsers, newTrials, directSubs, lostCustomers, renewedSubs, trialEvents, appTrialsConverted, appTrialsExpired] =
    await Promise.all([
      counted('newUsers'),
      counted('newTrials'),
      counted('directSubs'),
      counted('lostCustomers'),
      safe(() => renewalSeries(win, buckets)),
      // Both trial figures come out of one pass over the same event window.
      trialEventSeries(win, buckets).catch(() => null),
      counted('appTrialsConverted'),
      counted('appTrialsExpired'),
    ])

  return {
    newUsers,
    newTrials,
    // No shape for a figure that has no period — see `pointInTime`. Bucketing an
    // as-of-now count would draw eight identical points and call it a trend.
    trialsConverting: null,
    trialsCancelling: null,
    appTrialsRunning: null,
    appTrialsConverted,
    appTrialsExpired,
    directSubs,
    lostCustomers,
    renewedSubs,
    cancelledTrials: trialEvents?.cancelled ?? null,
    convertedTrials: trialEvents?.converted ?? null,
  }
}

/** Which bucket an epoch-ms timestamp falls in, or -1 when it is outside them. */
function bucketIndex(buckets: Bucket[], at: number): number {
  for (let i = 0; i < buckets.length; i++) {
    if (at >= buckets[i].from && at < buckets[i].to) return i
  }
  // The last bucket's end is the window's end, which is exclusive everywhere
  // else; a document landing exactly on it belongs to the last bucket rather
  // than to nothing.
  return buckets.length > 0 && at === buckets[buckets.length - 1].to ? buckets.length - 1 : -1
}

/** Renewals across the window, from one query rather than one per bucket. */
async function renewalSeries(win: Window, buckets: Bucket[]): Promise<number[]> {
  const { getDocs, orderBy, limit } = await import('firebase/firestore')
  const range: QueryConstraint[] = [where('currentPeriodStartsAtMs', '<', win.to)]
  if (win.from !== null) range.unshift(where('currentPeriodStartsAtMs', '>=', win.from))
  const snap = await getDocs(
    query(subs(), where('sandbox', '==', false), ...range, orderBy('currentPeriodStartsAtMs'), limit(10000)),
  )
  const out = buckets.map(() => 0)
  for (const d of snap.docs) {
    const cur = d.get('currentPeriodStartsAtMs')
    const start = d.get('startsAtMs')
    if (typeof cur !== 'number' || typeof start !== 'number' || cur <= start) continue
    const i = bucketIndex(buckets, cur)
    if (i >= 0) out[i]++
  }
  return out
}

/** Both trial transitions from one pass over the event log. */
async function trialEventSeries(
  win: Window,
  buckets: Bucket[],
): Promise<{ cancelled: number[]; converted: number[] }> {
  const { getDocs } = await import('firebase/firestore')
  const range: QueryConstraint[] = [where('eventAtMs', '<', win.to)]
  if (win.from !== null) range.unshift(where('eventAtMs', '>=', win.from))
  const snap = await getDocs(query(events(), where('sandbox', '==', false), ...range))
  const cancelled = buckets.map(() => 0)
  const converted = buckets.map(() => 0)
  for (const d of snap.docs) {
    const at = d.get('eventAtMs')
    if (typeof at !== 'number') continue
    const i = bucketIndex(buckets, at)
    if (i < 0) continue
    const type = String(d.get('type') ?? '')
    const period = String(d.get('periodType') ?? '').toUpperCase()
    // The same two tests `countEvents` makes, so a sparkline and the number
    // above it cannot disagree about what a trial transition is.
    if (period === 'TRIAL' && (type === 'CANCELLATION' || type === 'EXPIRATION')) cancelled[i]++
    if (d.get('isTrialConversion') === true) converted[i]++
  }
  return { cancelled, converted }
}

/** One metric, bucketed across the period. */
export async function loadSeries(
  key: MetricKey,
  win: Window,
  now: number = Date.now(),
): Promise<{ unit: BucketUnit; points: { label: string; value: number }[] }> {
  const { unit, buckets } = bucketsFor(win, now)
  const points = await Promise.all(
    buckets.map(async (b) => ({
      label: b.label,
      value: await countMetric(key, { from: b.from, to: b.to }, now),
    })),
  )
  return { unit, points }
}
